import { Ranges } from '../../util/getRangeValue';
import {
  ACTIVE_COUNTRIES,
  ACTIVE_COUNTRY_CODES,
  INACTIVE_COUNTRIES,
  INACTIVE_COUNTRY_CODES,
  ActiveCountryCode,
  InactiveCountryCode,
  AustralianRegions,
  NewZealandRegions,
  GreatBritainRegions,
} from '../configs/countries';

type RangesConfiguration = { ranges: Ranges; outOfRange: number };

export type EmissionCategories = {
  cafe_vehicles_tonnes: number;
  cafe_energy_tonnes: number;
  cafe_refrigeration_tonnes: number;
  cafe_electricity_tonnes: number;
  cafe_freight_tonnes: number;
  cafe_commuting_tonnes: number;
  cafe_water_tonnes: number;
  cafe_waste_tonnes: number;
  consultant_vehicles_tonnes: number;
  consultant_electricity_tonnes: number;
  consultant_airTravel_tonnes: number;
  consultant_accomodation_tonnes: number;
  consultant_taxisRentalCars_tonnes: number;
  consultant_commuting_tonnes: number;
  consultant_water_tonnes: number;
  consultant_waste_tonnes: number;
  consultant_vehicleRanges_tonnes: RangesConfiguration;
  consultant_accomodationRanges_tonnes: RangesConfiguration;

  ecommerce_vehicles_tonnes: number;
  ecommerce_electricity_tonnes: number;
  ecommerce_airTravel_tonnes: number;
  ecommerce_accomodation_tonnes: number;
  ecommerce_taxisRentalCars_tonnes: number;
  ecommerce_commuting_tonnes: number;
  ecommerce_freight_tonnes: number;
  ecommerce_water_tonnes: number;
  ecommerce_waste_tonnes: number;
  ecommerce_vehicleRanges_tonnes: RangesConfiguration;
  ecommerce_accomodationRanges_tonnes: RangesConfiguration;
  ecommerce_freightRanges_tonnes: RangesConfiguration;

  hairdresser_vehicles_tonnes: number;
  hairdresser_energy_tonnes: number;
  hairdresser_electricity_tonnes: number;
  hairdresser_freight_tonnes: number;
  hairdresser_commuting_tonnes: number;
  hairdresser_water_tonnes: number;
  hairdresser_waste_tonnes: number;
  homeoffice_total_tonnes: number;
  it_vehicles_tonnes: number;
  it_electricity_tonnes: number;
  it_airTravel_tonnes: number;
  it_accomodation_tonnes: number;
  it_taxisRentalCars_tonnes: number;
  it_commuting_tonnes: number;
  it_water_tonnes: number;
  it_waste_tonnes: number;
  it_vehicleRanges_tonnes: RangesConfiguration;
  it_accomodationRanges_tonnes: RangesConfiguration;
  motels_vehicles_tonnes: number;
  motels_energy_tonnes: number;
  motels_electricity_tonnes: number;
  motels_freight_tonnes: number;
  motels_commute_tonnes: number;
  motels_water_tonnes: number;
  motels_waste_tonnes: number;
  office_vehicles_tonnes: number;
  office_electricity_tonnes: number;
  office_airTravel_tonnes: number;
  office_accomodation_tonnes: number;
  office_taxisRentalCars_tonnes: number;
  office_commuting_tonnes: number;
  office_water_tonnes: number;
  office_waste_tonnes: number;
  office_vehicleRanges_tonnes: RangesConfiguration;
  office_accomodationRanges_tonnes: RangesConfiguration;
  restaurant_vehicles_tonnes: number;
  restaurant_energy_tonnes: number;
  restaurant_refrigeration_tonnes: number;
  restaurant_electricity_tonnes: number;
  restaurant_freight_tonnes: number;
  restaurant_commuting_tonnes: number;
  restaurant_water_tonnes: number;
  restaurant_waste_tonnesr: number;
  retail_vehicles_tonnes: number;
  retail_electricity_tonnes: number;
  retail_airTravel_tonnes: number;
  retail_freight_tonnes: number;
  retail_commuting_tonnes: number;
  retail_water_tonnes: number;
  retail_waste_tonnes: number;
};

export type CountryRegionEmissions<RegionKey extends string> = Record<
  RegionKey,
  EmissionCategories
>;

export type CountryEmissions = {
  NZ: CountryRegionEmissions<typeof NewZealandRegions[number]>;
  AU: CountryRegionEmissions<typeof AustralianRegions[number]>;
  GB: Partial<CountryRegionEmissions<typeof GreatBritainRegions[number]>>
};

export const COUNTRY_EMISSION_VALUES: CountryEmissions = {
  NZ: {
    DEFAULT: {
      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 5.91,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.5,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,
      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 0.22,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.12,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.75,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },

      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 0.26,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.12,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.75,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.01,
      ecommerce_waste_tonnes: 0.06,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 2.9,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.5,
      hairdresser_water_tonnes: 0.735,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 3.21,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 0.26,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.12,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.75,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 0.295,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.5,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 0.22,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.12,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.75,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.575,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 5.91,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.5,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,
      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 1.9,
      retail_airTravel_tonnes: 0.33680346,
      retail_freight_tonnes: 0.161,
      retail_commuting_tonnes: 0.5,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
  },
  AU: {
    NSW: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 2.04,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.4,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.0143,
      ecommerce_waste_tonnes: 0.0591,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },

      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 45.4,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.4,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 1.7,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.4,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 22.28,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.4,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 5.87,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 2.04,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.4,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 2.27,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 1.7,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.4,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 45.4,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.4,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 14.6,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
    QLD: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 2.04,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.6,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.0143,
      ecommerce_waste_tonnes: 0.0591,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },

      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 45.4,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.4,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 1.7,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.6,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 22.28,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.4,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 5.87,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 2.04,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.6,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 2.27,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 1.7,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.6,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 45.4,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.4,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 14.6,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
    SA: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 1.1,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.6,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.0143,
      ecommerce_waste_tonnes: 0.0591,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },
      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 24.7,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.4,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 0.92,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.6,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 12.1,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.4,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 4.76,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 1.1,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.6,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 1.23,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 0.92,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.6,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 24.7,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.4,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 7.9,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
    TAS: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 0.38,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.75,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.01,
      ecommerce_waste_tonnes: 0.06,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },

      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 8.4,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.5,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 0.32,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.75,
      consultant_water_tonnes: 0.01,
      consultant_waste_tonnes: 0.06,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 4.13,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.5,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 3.89,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 0.38,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.75,
      it_water_tonnes: 0.01,
      it_waste_tonnes: 0.06,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 0.42,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 0.32,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.75,
      office_water_tonnes: 0.01,
      office_waste_tonnes: 0.06,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 8.4,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.5,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 2.7,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
    VIC: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 2.57,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.4,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.0143,
      ecommerce_waste_tonnes: 0.0591,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },
      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 57.2,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.4,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 2.14,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.4,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 28.05,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.4,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 6.5,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 2.57,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.4,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 2.86,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 2.14,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.4,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 57.2,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.4,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 18.4,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
    WA: {
      ecommerce_vehicles_tonnes: 1.54,
      ecommerce_electricity_tonnes: 1.6,
      ecommerce_airTravel_tonnes: 0.73,
      ecommerce_accomodation_tonnes: 0.65,
      ecommerce_taxisRentalCars_tonnes: 0.04,
      ecommerce_commuting_tonnes: 0.6,
      ecommerce_freight_tonnes: 0.7,
      ecommerce_water_tonnes: 0.0143,
      ecommerce_waste_tonnes: 0.0591,
      ecommerce_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      ecommerce_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },
      ecommerce_freightRanges_tonnes: {
        ranges: [
          { to: 4, value: 0.7 },
          { to: 9, value: 1.05 },
          { to: 20, value: 2 },
        ],
        outOfRange: 3.5,
      },

      cafe_vehicles_tonnes: 1.54,
      cafe_energy_tonnes: 1.05,
      cafe_refrigeration_tonnes: 0.63,
      cafe_electricity_tonnes: 35.9,
      cafe_freight_tonnes: 0.161,
      cafe_commuting_tonnes: 0.4,
      cafe_water_tonnes: 0.495,
      cafe_waste_tonnes: 0.56,

      consultant_vehicles_tonnes: 4.82,
      consultant_electricity_tonnes: 1.34,
      consultant_airTravel_tonnes: 0.73,
      consultant_accomodation_tonnes: 0.65,
      consultant_taxisRentalCars_tonnes: 0.04,
      consultant_commuting_tonnes: 0.6,
      consultant_water_tonnes: 0.0143,
      consultant_waste_tonnes: 0.0591,
      consultant_vehicleRanges_tonnes: {
        ranges: [
          { to: 1, value: 1 },
          { to: 3, value: 2 },
          { to: 4, value: 3 },
          { to: 9, value: 6 },
          { to: 15, value: 11 },
          { to: 20, value: 13 },
        ],
        outOfRange: 0.65,
      },
      consultant_accomodationRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 10, value: 0.8 },
          { to: 20, value: 0.73 },
        ],
        outOfRange: 0.7,
      },

      hairdresser_vehicles_tonnes: 1.54,
      hairdresser_energy_tonnes: 2.3,
      hairdresser_electricity_tonnes: 17.6,
      hairdresser_freight_tonnes: 0.161,
      hairdresser_commuting_tonnes: 0.4,
      hairdresser_water_tonnes: 0.74,
      hairdresser_waste_tonnes: 0.56,

      homeoffice_total_tonnes: 5.36,

      it_vehicles_tonnes: 1.54,
      it_electricity_tonnes: 1.6,
      it_airTravel_tonnes: 0.73,
      it_accomodation_tonnes: 0.65,
      it_taxisRentalCars_tonnes: 0.04,
      it_commuting_tonnes: 0.6,
      it_water_tonnes: 0.0143,
      it_waste_tonnes: 0.0591,
      it_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },
      it_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      motels_vehicles_tonnes: 1.54,
      motels_energy_tonnes: 0.871,
      motels_electricity_tonnes: 1.79,
      motels_freight_tonnes: 0.161,
      motels_commute_tonnes: 0.4,
      motels_water_tonnes: 0.063,
      motels_waste_tonnes: 0.21,

      office_vehicles_tonnes: 1.54,
      office_electricity_tonnes: 1.34,
      office_airTravel_tonnes: 0.73,
      office_accomodation_tonnes: 0.65,
      office_taxisRentalCars_tonnes: 0.04,
      office_commuting_tonnes: 0.6,
      office_water_tonnes: 0.0143,
      office_waste_tonnes: 0.0591,
      office_vehicleRanges_tonnes: {
        ranges: [
          { to: 4, value: 1 },
          { to: 9, value: 2 },
          { to: 20, value: 4 },
        ],
        outOfRange: 5,
      },

      office_accomodationRanges_tonnes: {
        ranges: [{ to: 4, value: 1 }],
        outOfRange: 0.33,
      },

      restaurant_vehicles_tonnes: 1.54,
      restaurant_energy_tonnes: 1.58,
      restaurant_refrigeration_tonnes: 0.63,
      restaurant_electricity_tonnes: 35.9,
      restaurant_freight_tonnes: 0.161,
      restaurant_commuting_tonnes: 0.4,
      restaurant_water_tonnes: 0.495,
      restaurant_waste_tonnesr: 0.56,

      retail_vehicles_tonnes: 1.54,
      retail_electricity_tonnes: 11.5,
      retail_airTravel_tonnes: 0.73,
      retail_freight_tonnes: 0.65,
      retail_commuting_tonnes: 0.4,
      retail_water_tonnes: 0.0143,
      retail_waste_tonnes: 0.0591,
    },
  },
  GB: {
  }
};

export type Countries = keyof typeof COUNTRY_EMISSION_VALUES;

// export type InactiveCountries =
//   | 'AT'
//   | 'BE'
//   | 'BR'
//   | 'CA'
//   | 'CARIBBEAN'
//   | 'CL'
//   | 'CN'
//   | 'HK'
//   | 'CO'
//   | 'CR'
//   | 'CZ'
//   | 'EG'
//   | 'FR'
//   | 'DE'
//   | 'IN'
//   | 'ID'
//   | 'IE'
//   | 'IT'
//   | 'JP'
//   | 'JO'
//   | 'MY'
//   | 'MX'
//   | 'NL'
//   | 'PA'
//   | 'PL'
//   | 'PT'
//   | 'QA'
//   | 'RU'
//   | 'SA'
//   | 'SG'
//   | 'ZA'
//   | 'KR'
//   | 'ES'
//   | 'CH'
//   | 'TH'
//   | 'TR'
//   | 'AE'
//   | 'GB'
//   | 'US'
//   | 'VN';

export type CountryRegion<
  ACountry extends Countries
> = keyof typeof COUNTRY_EMISSION_VALUES[ACountry];

export type CountryRegionType<
  ACountry extends Countries,
  ARegion extends keyof CountryEmissions[ACountry]
> = { country: ACountry; region: ARegion };

// user defined type guard
function isEmissionCategories(
  emissionCategories: any,
): emissionCategories is EmissionCategories {
  //TODO: better testing
  return !!emissionCategories;
}

export const isActiveCountry = (
  countryCode: any,
): countryCode is ActiveCountryCode => {
  return ACTIVE_COUNTRY_CODES.includes(countryCode);
};

export const isInactiveCountry = (
  countryCode: any,
): countryCode is InactiveCountryCode => {
  return INACTIVE_COUNTRY_CODES.includes(countryCode);
};

export const isActiveCountryRegion = <
  ACountry extends Countries,
  ACountryRegion extends CountryRegion<ACountry>
>(
  country: ACountry,
  region: any,
): region is ACountryRegion => {
  const countryRegions = Object.keys(COUNTRY_EMISSION_VALUES[country]);
  return countryRegions.includes(region);
};

export const parseCountry = (country: string): Countries => {
  if (!isActiveCountry(country))
    throw Error(`Supplied country ${country} is not a current valid option`);
  return country;
};

export const parseCountryRegion = <ACountry extends Countries>(
  country: ACountry,
  region: any,
): keyof CountryEmissions[ACountry] => {
  if (!isActiveCountryRegion(country, region))
    throw Error(
      `Supplied region, ${region}, in country ${country} is not a current valid option`,
    );
  return region;
};

export const getEmissionsByCountryRegion = <
  Country extends Countries,
  Region extends CountryRegion<Country>
>(
  country: Country,
  region: Region,
): EmissionCategories => {
  const chosenCountry = COUNTRY_EMISSION_VALUES[country];
  const emissionCategories = chosenCountry[region];
  if (!isEmissionCategories(emissionCategories)) {
    throw Error(`The shape of ${country}/${region} isn't correct`);
  }
  return emissionCategories;
};

export const throwIfAnyUndefined = (values: any[], fromName: string): void => {
  if (values.some((value) => value === undefined)) {
    throw Error(
      `Required values from ${fromName} not found in ${JSON.stringify(
        values,
      )}.`,
    );
  }
};

export const getActiveCountryByCode = (countryCode: ActiveCountryCode) => {
  const country = ACTIVE_COUNTRIES.find(({ code }) => code === countryCode);
  if (!country) throw Error(`${countryCode} is not a valid country code`);
  return country;
};

export const getInActiveCountryByCode = (countryCode: InactiveCountryCode) => {
  const country = INACTIVE_COUNTRIES.find(({ code }) => code === countryCode);
  if (!country) throw Error(`${countryCode} is not a valid country code`);
  return country;
};

export const getCountryByCode = (
  countryCode: ActiveCountryCode | InactiveCountryCode,
) => {
  if (isActiveCountry(countryCode)) {
    return getActiveCountryByCode(countryCode);
  } else if (isInactiveCountry(countryCode)) {
    return getInActiveCountryByCode(countryCode);
  }
  throw Error(`Unknown country ${countryCode}`);
};
